import { get } from 'svelte/store';

import { changeLanguage, t } from '@translation';

import { extractResponse } from '@core/api';
import { Path, router } from '@core/router';

import { loginApi } from '@shared/apis';
import { STORAGE_KEYS } from '@shared/constants';
import { toasts } from '@shared/features';
import { type IProfile, type IServiceData } from '@shared/models';
import { activeProfile, profiles } from '@shared/stores';

import { storageService } from './storageService';

class AuthService {
  constructor() {}

  public getMe({ payload: token, onSuccess, onError }: IServiceData<string>): void {
    loginApi
      .getMe(token)
      .then(async res => {
        const profileData = extractResponse(res)?.item;

        changeLanguage(profileData.language);
        activeProfile.set(profileData);
        await storageService.setToken(token);
        await this.handleStorageProfiles(token, profileData);

        onSuccess?.(profileData);
      })
      .catch(async err => {
        if (err.response?.status === 401) {
          const profilesData = get(profiles);

          if (profilesData) {
            const profilesDataCopy = { ...profilesData };
            delete profilesDataCopy[token];

            await storageService.setItem(
              STORAGE_KEYS.ProfilesData,
              JSON.stringify(profilesDataCopy),
            );
            profiles.set(profilesDataCopy);
          }

          toasts.send({
            message: get(t)('errors.not-possible-auth'),
            type: 'error',
          });

          if (storageService.cachedToken === token) {
            await storageService.removeToken();
            activeProfile.set(null);
          }
        } else {
          toasts.send({
            message: get(t)('errors.unknown'),
            type: 'error',
          });
        }
        onError?.(err);
      });
  }

  private async handleStorageProfiles(token: string, user: IProfile): Promise<void> {
    const profilesData = get(profiles);

    if (profilesData) {
      const updatedProfilesData = {
        ...profilesData,
        [token]: user,
      };
      await storageService.setItem(STORAGE_KEYS.ProfilesData, JSON.stringify(updatedProfilesData));
      profiles.set(updatedProfilesData);
    } else {
      const userData = {
        [token]: user,
      };
      await storageService.setItem(STORAGE_KEYS.ProfilesData, JSON.stringify(userData));
      profiles.set(userData);
    }
  }

  public async logout(isSingle = true): Promise<void> {
    const activeToken = storageService.cachedToken || '';

    const storageUsersData = { ...get(profiles) };

    if (isSingle) {
      delete storageUsersData[activeToken];
      profiles.update(prev => {
        const prevCopy = { ...prev };
        delete prevCopy[activeToken];
        return prevCopy;
      });
      await storageService.setItem(STORAGE_KEYS.ProfilesData, JSON.stringify(get(profiles)));
    } else {
      await storageService.removeItem(STORAGE_KEYS.ProfilesData);
      profiles.set({});
    }

    await storageService.removeToken();

    activeProfile.set(null);

    if (Object.keys(storageUsersData).length && isSingle) {
      router.navigate(Path.PROFILES);
    } else {
      router.navigate(Path.WELCOME);
    }
  }

  public static instance: AuthService | null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }
}

export const authService = AuthService.getInstance();
